import { faPaperPlane, faRobot, faSpinner, faStop } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormEvent, useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { profileAiChatChannelName } from '@/channels/utils/channelNames';
import AppContext from '@/contexts/appContext';
import { useChannelData } from '@/hooks/useChannel';
import PersonContext from '@/pages/Person/PersonContext';
import ProfileChatRepository, { ChatHistoryResponse } from '@/repositories/ProfileChatRepository';
import Button from '@/ui/components/Button';
import Container from '@/ui/components/Container';
import InputField from '@/ui/components/Form/InputField';
import Heading from '@/ui/components/Heading';

import AiChatMessage from './components/AiChatMessage';
import { SampleQuestion } from './components/SampleQuestion';
import { AI_PROFILE_EXAMPLE_QUESTIONS } from './constants';
import { ChatHistory } from './types';

type ChunkKey = 'data' | 'response';

interface ChunkBufferEntry {
  chunks: Array<string | undefined>;
  chunkCount: number;
  chunkKey: ChunkKey;
  chunkValueJson: boolean;
  message: ChatHistoryResponse;
}

const PersonAiProfile = () => {
  const { currentUser, config } = useContext(AppContext);
  const { person } = useContext(PersonContext);
  const chatRepo = ProfileChatRepository();
  const [searchParams] = useSearchParams();
  const autosubmitFired = useRef(false);
  const chunkBuffer = useRef<Record<string, ChunkBufferEntry>>({});
  const contextMessageId = useRef<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef(true);
  const programmaticScrollRef = useRef(false);

  const initialPrompt = searchParams.get('prompt') ?? '';
  const autosubmit = searchParams.get('autosubmit') === 'true';
  const tools = searchParams.get('tools')?.split(',').filter(Boolean) ?? [];
  const context = searchParams.get('context')?.split(',').filter(Boolean) ?? [];

  const [currentQuestion, setCurrentQuestion] = useState<string>(initialPrompt);

  const [history, setHistory] = useState<ChatHistory[]>([]);
  const [streaming, setStreaming] = useState<boolean>(false);
  const [loadingPhase, setLoadingPhase] = useState<'submitting' | 'context' | null>(null);
  const [reset, setReset] = useState<boolean>(false);

  // Websocket channel updates
  const { channelData } = useChannelData<ChatHistoryResponse>({
    channelId: profileAiChatChannelName(person.id, currentUser?.id),
    channelName: 'profile AI chat'
  });

  const reassembleChannelData = (data: ChatHistoryResponse): ChatHistoryResponse | null => {
    if (!data.chunked) {
      return data;
    }

    const { chunk_count, chunk_id, chunk_index, chunk_key, chunk_value_json } = data;

    if (!chunk_id || chunk_index === undefined || !chunk_count || !chunk_key) {
      return null;
    }

    const existing = chunkBuffer.current[chunk_id] ?? {
      chunks: new Array<string | undefined>(chunk_count),
      chunkCount: chunk_count,
      chunkKey: chunk_key,
      chunkValueJson: !!chunk_value_json,
      message: data
    };

    if (chunk_index < 0 || chunk_index >= chunk_count) {
      return null;
    }

    const chunkValue = chunk_key === 'response' ? data.response : data.data;
    existing.chunks[chunk_index] = String(chunkValue ?? '');
    existing.message = data;
    chunkBuffer.current[chunk_id] = existing;

    if (existing.chunks.filter((chunk) => chunk !== undefined).length !== existing.chunkCount) {
      return null;
    }

    const combinedValue = existing.chunks.join('');
    const reconstructedMessage = {
      ...existing.message,
      chunked: false,
      chunk_id: undefined,
      chunk_index: undefined,
      chunk_count: undefined,
      chunk_key: undefined,
      chunk_value_json: undefined
    };

    if (existing.chunkKey === 'response') {
      reconstructedMessage.response = combinedValue;
    } else if (existing.chunkValueJson) {
      try {
        reconstructedMessage.data = JSON.parse(combinedValue);
      } catch {
        reconstructedMessage.data = combinedValue;
      }
    } else {
      reconstructedMessage.data = combinedValue;
    }

    delete chunkBuffer.current[chunk_id];
    return reconstructedMessage;
  };

  useEffect(() => {
    if (channelData) {
      const completeChannelData = reassembleChannelData(channelData);

      if (!completeChannelData) {
        return;
      }

      const messageType = completeChannelData?.type;

      if (messageType === 'context_prompt') {
        // Backend sends context_prompt FIRST — create the context message here
        setLoadingPhase('context');
        const ctxId = uuidv4();
        contextMessageId.current = ctxId;
        setHistory((prev) => [
          ...prev,
          {
            agent: 'context',
            content: '',
            session_id: ctxId,
            context_prompt: completeChannelData.data as string
          }
        ]);
      } else if (messageType === 'context_request') {
        // Backend sends context_request SECOND — update the existing context message
        const ctxId = contextMessageId.current;
        if (ctxId) {
          setHistory((prev) =>
            prev.map((item) =>
              item.session_id === ctxId
                ? { ...item, context_request: completeChannelData.data as Record<string, boolean> }
                : item
            )
          );
        }
      } else if (messageType === 'full_prompt') {
        const ctxId = contextMessageId.current;
        if (ctxId) {
          // Append the full prompt to an existing context message from context_request
          setHistory((prev) =>
            prev.map((item) =>
              item.session_id === ctxId
                ? { ...item, full_prompt: completeChannelData.data as string }
                : item
            )
          );
        } else {
          // No prior context_request — surface full_prompt as its own context message
          const newCtxId = uuidv4();
          contextMessageId.current = newCtxId;
          setHistory((prev) => [
            ...prev,
            {
              agent: 'context',
              content: '',
              session_id: newCtxId,
              full_prompt: completeChannelData.data as string
            }
          ]);
        }
      }

      // If the response has a session id, streaming has started or finished
      if (completeChannelData?.session_id) {
        setLoadingPhase(null);
        if (completeChannelData.status === 'streaming') {
          setStreaming(true);
        } else {
          setStreaming(false);
        }

        const sessionId = completeChannelData.session_id;
        setHistory((prev) => {
          const existingIndex = prev.findIndex((item) => item.session_id === sessionId);
          if (existingIndex !== -1) {
            return prev.map((item, index) =>
              index === existingIndex ? { ...item, content: completeChannelData.response } : item
            );
          }
          return [
            ...prev,
            {
              content: completeChannelData.response,
              agent: 'assistant',
              session_id: sessionId
            }
          ];
        });
      }
    }
    // Do not set history as a dependency or else it will cause an infinite loop
  }, [channelData]);

  // Suppress auto-scroll lock changes while we're programmatically scrolling.
  useEffect(() => {
    const onScroll = () => {
      if (programmaticScrollRef.current) return;
      const distanceFromBottom =
        document.documentElement.scrollHeight - window.scrollY - window.innerHeight;
      autoScrollRef.current = distanceFromBottom < 80;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!autoScrollRef.current) return;
    // During streaming use auto so rapid chunk updates don't restart a smooth animation.
    // For new messages (not yet streaming) use smooth for a polished feel.
    const behavior = streaming ? 'auto' : 'smooth';
    programmaticScrollRef.current = true;
    messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
    const t = setTimeout(() => {
      programmaticScrollRef.current = false;
    }, 500);
    return () => clearTimeout(t);
  }, [history, streaming]);

  const resetHistory = (reset: boolean) => {
    setHistory([]);
    setReset(reset);
    contextMessageId.current = null;
  };

  const handleAskQuestion = async (question?: string) => {
    if (!currentQuestion && !question) {
      return;
    }

    const questionToAsk = question || currentQuestion;
    const userMessage: ChatHistory = {
      content: questionToAsk,
      agent: 'user',
      session_id: uuidv4()
    };
    contextMessageId.current = null;
    autoScrollRef.current = true;
    setCurrentQuestion('');
    setHistory((prev) => [...prev, userMessage]);
    setLoadingPhase('submitting');

    try {
      await chatRepo.ask({
        id: person.id,
        query: questionToAsk,
        reset,
        stream: true,
        tools,
        context
      });
    } catch {
      setHistory((prev) => prev.filter((item) => item.session_id !== userMessage.session_id));
      setLoadingPhase(null);
    } finally {
      setReset(false);
    }
  };

  useEffect(() => {
    if (autosubmit && initialPrompt && !autosubmitFired.current) {
      autosubmitFired.current = true;
      void handleAskQuestion(initialPrompt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fillAndAskQuestion = async (question: string) => {
    setCurrentQuestion(question);
    await handleAskQuestion(question);
  };

  const submitForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handleAskQuestion();
  };

  const handleAbort = async () => {
    // Grab the most recent session id
    const lastMessage = history[history.length - 1];
    if (!lastMessage) {
      return;
    }
    await chatRepo.abort({ sessionId: String(lastMessage?.session_id) });
  };

  return (
    <Container type="page">
      <div className="flex flex-col items-center justify-center w-full h-full">
        <Heading text={'Chat'} value={2} />
        {config.ai_provider && (
          <p className="text-xs text-gray-400 mb-2">
            {config.ai_provider} &mdash; {config.ai_model_name}
          </p>
        )}

        <div className="flex flex-col items-center">
          {history.map((item, index) => (
            <AiChatMessage key={index} item={item} />
          ))}

          {history.length === 0 && (
            // Give a couple of examples of questions that can be asked, clicking them to ask the question
            <ul className="max-w-[50vw]">
              <h4 className="italic">Examples</h4>
              {AI_PROFILE_EXAMPLE_QUESTIONS.map((example, index) => (
                <li key={index}>
                  <SampleQuestion
                    label={example.label}
                    question={example.question}
                    onClick={fillAndAskQuestion}
                  />
                </li>
              ))}
            </ul>
          )}

          {loadingPhase === 'submitting' && (
            <div className="flex flex-row-reverse ml-4 bg-blue-100 rounded-xs p-3 mb-2 shadow-sm items-center">
              <div className="mr-4 shrink-0">
                <div className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-500">
                  <FontAwesomeIcon className="text-white" icon={faRobot} />
                </div>
              </div>
              <div className="mr-4 shrink-0">
                <FontAwesomeIcon icon={faSpinner} spin={true} size="lg" />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="sticky bottom-0 z-10 w-full bg-gray-100 dark:bg-gray-800 border-t-2 border-gray-200 dark:border-gray-700 py-4 px-3">
          <form onSubmit={submitForm} className="flex flex-row  items-center ">
            <div className="grow">
              <InputField
                id="question"
                type="text"
                placeholder="Ask a question"
                value={currentQuestion}
                wrapperClassNames="w-full"
                onChange={(event) => setCurrentQuestion(event?.target?.value)}
              />
            </div>

            <div className="flex flex-row items-center justify-center grow-0">
              {streaming ? (
                <Button className="inline-block" onClick={() => handleAbort()} ariaLabel="Halt">
                  <FontAwesomeIcon icon={faStop} />
                </Button>
              ) : (
                <>
                  <Button
                    className="inline-block mx-2"
                    type="submit"
                    ariaLabel="Send"
                    disabled={loadingPhase !== null}>
                    <FontAwesomeIcon icon={faPaperPlane} />
                  </Button>
                  <Button
                    className="inline-block"
                    onClick={() => resetHistory(true)}
                    color="white"
                    disabled={loadingPhase !== null}>
                    Reset
                  </Button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    </Container>
  );
};

export default PersonAiProfile;
